import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Chip, Switch, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, Link,
} from '@mui/material'
import { Add, Edit, Delete, Lock, LockOpen, OpenInNew } from '@mui/icons-material'
import { useApi, apiPost, apiPut, apiDelete } from '../../api/client'
import { fetchRecords } from '../../api/records'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { useToastStore } from '../../stores/toast.store'
import type { ApiResponse } from '@crm/shared'
import { TIMEZONES } from '../../utils/timezones'

interface UserRow {
  id: string
  username: string
  email: string | null
  display_name: string | null
  is_active: number
  is_tenant_admin: number
  is_locked: number
  must_change_password: number
  timezone: string
  role_ids: string[]
  employee_id: number | null
}

interface RoleOption {
  id: string
  name: string
}

interface EmployeeOption {
  seq_id: number
  name: string
}

export function UserManagement() {
  const navigate = useNavigate()
  const { data, mutate } = useApi<ApiResponse<UserRow[]>>('/users')
  const users = data?.data ?? []
  const toast = useToastStore()

  const { data: rolesData } = useApi<ApiResponse<RoleOption[]>>('/roles')
  const roles = rolesData?.data ?? []

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState<UserRow | null>(null)
  const [form, setForm] = useState({
    username: '',
    email: '',
    display_name: '',
    password: '',
    is_tenant_admin: false,
    is_locked: false,
    timezone: 'UTC',
    role_ids: [] as string[],
    employee_id: null as number | null,
  })
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [allEmployees, setAllEmployees] = useState<EmployeeOption[]>([])
  const [loadingEmployees, setLoadingEmployees] = useState(false)

  useEffect(() => {
    if (dialogOpen) {
      setLoadingEmployees(true)
      fetchRecords('obj_employees', { limit: '500' })
        .then((res) => {
          setAllEmployees(
            res.data.map((r) => ({ seq_id: Number(r.id), name: r.data.name as string })),
          )
        })
        .catch(() => setAllEmployees([]))
        .finally(() => setLoadingEmployees(false))
    } else {
      setAllEmployees([])
    }
  }, [dialogOpen])

  const employees = useMemo<EmployeeOption[]>(() => {
    const boundIds = new Set(users.map((u) => u.employee_id).filter(Boolean))
    return allEmployees.filter(
      (emp) => !boundIds.has(emp.seq_id) || (editUser && emp.seq_id === editUser.employee_id),
    )
  }, [allEmployees, users, editUser])

  const openCreate = () => {
    setEditUser(null)
    setForm({ username: '', email: '', display_name: '', password: '', is_tenant_admin: false, is_locked: false, timezone: 'UTC', role_ids: [], employee_id: null })
    setDialogOpen(true)
  }

  const openEdit = (user: UserRow) => {
    setEditUser(user)
    setForm({
      username: user.username,
      email: user.email ?? '',
      display_name: user.display_name ?? '',
      password: '',
      is_tenant_admin: !!user.is_tenant_admin,
      is_locked: !!user.is_locked,
      timezone: user.timezone || 'UTC',
      role_ids: user.role_ids ?? [],
      employee_id: user.employee_id,
    })
    setDialogOpen(true)
  }

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      if (editUser) {
        const payload: Record<string, unknown> = {
          email: form.email || null,
          display_name: form.display_name,
          is_tenant_admin: form.is_tenant_admin,
          is_locked: form.is_locked,
          timezone: form.timezone,
          role_ids: form.role_ids,
        }
        if (editUser.employee_id === null && form.employee_id) {
          payload.employee_id = form.employee_id
        }
        await apiPut(`/users/${editUser.id}`, payload)
        if (form.password) {
          await apiPut(`/users/${editUser.id}/password`, { password: form.password })
        }
      } else {
        await apiPost('/users', {
          username: form.username,
          email: form.email || null,
          display_name: form.display_name,
          password: form.password,
          is_tenant_admin: form.is_tenant_admin,
          timezone: form.timezone,
          role_ids: form.role_ids,
        })
      }
      mutate()
      setDialogOpen(false)
      toast.success(editUser ? '用户已更新' : '用户已创建')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }, [editUser, form, mutate])

  const handleToggleActive = useCallback(async (user: UserRow) => {
    try {
      await apiPut(`/users/${user.id}`, { is_active: !user.is_active })
      mutate()
      toast.success(user.is_active ? '用户已停用' : '用户已启用')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '操作失败')
    }
  }, [mutate, toast])

  const handleDelete = useCallback(async (id: string) => {
    try {
      await apiDelete(`/users/${id}`)
      mutate()
      toast.success('用户已删除')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败')
    }
    setDeleteConfirm(null)
  }, [mutate])

  const handleRoleToggle = (roleId: string) => {
    setForm((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((id) => id !== roleId)
        : [...prev.role_ids, roleId],
    }))
  }

  const navigateToEmployee = (seqId: number) => {
    navigate(`/o/obj_employees/${seqId}`)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Key Users</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={openCreate}>New User</Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Roles</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Employee ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Locked</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 100, whiteSpace: 'nowrap' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.display_name ?? '—'}</TableCell>
                <TableCell>{user.email ?? '—'}</TableCell>
                <TableCell>
                  {!!user.is_tenant_admin && <Chip label="Admin" size="small" color="primary" sx={{ mr: 0.5 }} />}
                  {(user.role_ids ?? []).map((roleId) => {
                    const role = roles.find((r) => r.id === roleId)
                    return role ? <Chip key={roleId} label={role.name} size="small" variant="outlined" sx={{ mr: 0.5 }} /> : null
                  })}
                </TableCell>
                <TableCell>
                  {user.employee_id ? (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigateToEmployee(user.employee_id!)}
                      sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {user.employee_id}
                    </Link>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell><Switch checked={!!user.is_active} size="small" onChange={() => handleToggleActive(user)} /></TableCell>
                <TableCell>{!!user.is_locked ? <Lock color="error" fontSize="small" /> : <LockOpen color="disabled" fontSize="small" />}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton size="small" onClick={() => openEdit(user)}><Edit fontSize="small" /></IconButton>
                  {!user.is_tenant_admin && <IconButton size="small" color="error" onClick={() => setDeleteConfirm(user.id)}><Delete fontSize="small" /></IconButton>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit User' : 'New User'}</DialogTitle>
        <DialogContent>
          <TextField label="Username" value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} fullWidth size="small" sx={{ mt: 1 }} disabled={!!editUser} />
          <TextField label="Display Name" value={form.display_name} onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))} fullWidth size="small" sx={{ mt: 2 }} />
          <TextField label="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} fullWidth size="small" sx={{ mt: 2 }} />
          <TextField label={editUser ? 'New Password (leave blank to keep)' : 'Password'} type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} fullWidth size="small" sx={{ mt: 2 }} required={!editUser} autoComplete="new-password" />
          <FormControlLabel control={<Switch checked={form.is_tenant_admin} onChange={(e) => setForm((p) => ({ ...p, is_tenant_admin: e.target.checked }))} />} label="Tenant Admin" sx={{ mt: 1 }} />
          {editUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">Locked:</Typography>
              <Chip
                icon={form.is_locked ? <Lock /> : <LockOpen />}
                label={form.is_locked ? 'Locked' : 'Unlocked'}
                color={form.is_locked ? 'error' : 'success'}
                size="small"
                onClick={() => setForm((p) => ({ ...p, is_locked: !p.is_locked }))}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          )}
          <FormControl size="small" fullWidth sx={{ mt: 2 }}>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={form.timezone}
              onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
              label="Timezone"
            >
              {TIMEZONES.map((tz: string) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            {editUser && editUser.employee_id ? (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Bound Employee (read-only)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={editUser.employee_id}
                    size="small"
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ width: 130 }}
                  />
                  <IconButton size="small" onClick={() => navigateToEmployee(editUser.employee_id!)} title="Open employee">
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ) : editUser ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Bind to Employee</InputLabel>
                <Select
                  value={form.employee_id ?? ''}
                  label="Bind to Employee"
                  onChange={(e) => setForm((p) => ({ ...p, employee_id: e.target.value ? Number(e.target.value) : null }))}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {loadingEmployees ? (
                    <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> Loading...</MenuItem>
                  ) : (
                    employees.map((emp) => (
                      <MenuItem key={emp.seq_id} value={emp.seq_id}>
                        {emp.seq_id} — {emp.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" color="text.secondary">
                An Employee record will be automatically created and linked.
              </Typography>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Assigned Roles</Typography>
          {roles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No roles available</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {roles.map((role) => (
                <Chip
                  key={role.id}
                  label={role.name}
                  variant={form.role_ids.includes(role.id) ? 'filled' : 'outlined'}
                  color={form.role_ids.includes(role.id) ? 'primary' : 'default'}
                  onClick={() => handleRoleToggle(role.id)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => handleDelete(deleteConfirm!)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  )
}
